import { Component, ErrorInfo, ReactNode } from "react";
import { ErrorState } from "@/components/site/ErrorState";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  /** Optional label shown in console for easier debugging */
  label?: string;
  /** "page" renders a full-screen friendly error; "section" stays inline. */
  variant?: "page" | "section";
}

interface State {
  hasError: boolean;
  message?: string;
}

/**
 * Catches render-time errors in children so a single broken section
 * (e.g. a third-party embed) cannot blank-screen the entire site.
 * Falls back to the shared friendly ErrorState with a working way forward.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, message: error.message };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error(`[ErrorBoundary${this.props.label ? `:${this.props.label}` : ""}]`, error, info);
  }

  private reset = () => {
    this.setState({ hasError: false, message: undefined });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;

      const isPage = this.props.variant === "page";
      return (
        <ErrorState
          variant={isPage ? "page" : "section"}
          kicker={isPage ? "Unexpected error" : "Section temporarily unavailable"}
          title={isPage ? "This page didn't load correctly" : "This section didn't load"}
          description={
            isPage
              ? "Something broke while rendering this page. Try again — or head to the resources library, which is always available."
              : "The rest of the page still works. Try again, or browse the resources library."
          }
          onRetry={this.reset}
        />
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
